import {
  useEmailConfirmationMutation,
  useForgotPasswordMutation,
  useLoginMutation,
  useMeQuery,
  useRegisterMutation,
  useResetPasswordMutation,
  UsersPermissionsLoginInput,
  UsersPermissionsRegisterInput,
  useUsersPermissionsUserQuery,
} from "@/generated";
import { makeVar, useReactiveVar } from "@apollo/client";

export const tokenVar = makeVar<string | null>(null);

const useAuthUtils = () => {
  const token = useReactiveVar(tokenVar);

  const user = useMeQuery({
    skip: token === null,
  });

  const userAttributes = useUsersPermissionsUserQuery({
    fetchPolicy: "cache-and-network",
    skip: !user.data?.me?.id,
    variables: { id: user.data?.me?.id ?? "" },
  });

  const [
    loginMutation,
    { data: loginData, loading: loginLoading, error: loginError },
  ] = useLoginMutation();
  const [
    registerMutation,
    { data: registerData, loading: registerLoading, error: registerError },
  ] = useRegisterMutation();
  const [
    forgotPasswordMutation,
    {
      data: forgotPassData,
      loading: forgotPasswordLoading,
      error: forgotPasswordError,
    },
  ] = useForgotPasswordMutation();
  const [
    resetPasswordMutation,
    {
      data: resetPasswordData,
      loading: resetPasswordLoading,
      error: resetPasswordError,
    },
  ] = useResetPasswordMutation();
  const [
    emailConfirmationMutation,
    {
      data: emailConfirmationData,
      loading: emailConfirmationLoading,
      error: emailConfirmationError,
    },
  ] = useEmailConfirmationMutation();

  const signIn = (inputVariables: UsersPermissionsLoginInput) => {
    try {
      loginMutation({
        variables: { input: inputVariables },
      }).catch((e) => console.error(e));
    } catch (e) {
      console.error(e);
    }
  };

  const signUp = async (
    inputVariables: UsersPermissionsRegisterInput,
    token: string = ""
  ) => {
    try {
      await registerMutation({
        variables: { input: inputVariables },
        context: { headers: { "g-recaptcha-token": token } },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const forgotPassword = (email: string) => {
    try {
      forgotPasswordMutation({
        variables: { email },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const resetPassword = (
    password: string,
    passwordConfirmation: string,
    code: string
  ) => {
    try {
      resetPasswordMutation({
        variables: { password, passwordConfirmation, code },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const confirmEmail = (confirmation: string) => {
    try {
      emailConfirmationMutation({
        variables: { confirmation },
      });
    } catch (e) {
      console.error(e);
    }
  };

  return {
    signIn,
    signUp,
    forgotPassword,
    resetPassword,
    confirmEmail,
    loginResponse: loginData,
    registerResponse: registerData,
    forgotPasswordResponse: forgotPassData,
    resetPasswordResponse: resetPasswordData,
    confirmEmailResponse: emailConfirmationData,
    loading:
      loginLoading ||
      registerLoading ||
      forgotPasswordLoading ||
      resetPasswordLoading ||
      emailConfirmationLoading,
    error:
      loginError ||
      registerError ||
      forgotPasswordError ||
      resetPasswordError ||
      emailConfirmationError,
    userAttributes,
    token,
    user,
  };
};

export default useAuthUtils;
